import { EHandlePdfBlobMode, ITEM_MIMETYPES, TItemType } from '@modules/items';
import { blobHref, proxyUrl } from '../utils';
import { downloadFile } from '../../modules/files';
import DOMPurify from 'dompurify';
import { calculateRowsPerPage } from './calculateRowsPerPage';

const toTimestampString = (dateString?: string) => {
  const s = dateString ? new Date(dateString).toISOString().replace('T', ' ').substring(0, 19) : '?';
  return s + ' (UTC+00:00)';
};

const getQueryKey = (itemType: TItemType, itemId: string) => {
  return ['REPLICA_DOCUMENT', 'REPLICA_FILE'].includes(itemType)
    ? `replica-${itemId}`
    : `${itemType.toLowerCase()}-${itemId}`;
};

/**
 * Sort descending by date string. Returns negative if `b` is later than `a`.
 */
function sortByDateDesc(aDate?: string, bDate?: string): number {
  const aTime = aDate ? new Date(aDate).getTime() : 0;
  const bTime = bDate ? new Date(bDate).getTime() : 0;
  return bTime - aTime; // descending
}

const toUniqueArrayByKey = <T>(array: T[], getKey: (item: T) => unknown): T[] => [
  ...new Map(array.map((item) => [getKey(item), item])).values(),
];

const areArraysEqual = <T>(a: T[], b: T[]) => a.length === b.length && a.every((elemA, i) => elemA === b[i]);

const shallowEqual = <T>(a: T, b: T): boolean => {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (typeof a !== 'object' || typeof b !== 'object') return false;

  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;

  for (const key of keysA) {
    if (!Object.prototype.hasOwnProperty.call(b, key)) return false;
    if ((a as Record<string, unknown>)[key] !== (b as Record<string, unknown>)[key]) return false;
  }
  return true;
};

/* helper that both view & download functions can reuse */
const handlePdfBlob = (
  containerId: string,
  blobName: string,
  token: string,
  mode: EHandlePdfBlobMode,
  filename: string
) => {
  const href = blobHref(containerId, blobName, token);
  const mimetype = ITEM_MIMETYPES.pdf[0];

  if (mode === EHandlePdfBlobMode.VIEW) {
    window.open(proxyUrl(href, filename, mimetype, true), '_blank');
  } else {
    downloadFile(href, mimetype, filename);
  }
};

/**
 * Sanitizes HTML with <em> tags.
 * Keeps only <em> tags for highlights.
 * Strips all other tags and attributes to prevent XSS.
 *
 * Used for:
 * - Azure AI Search snippets
 * - Reference detection context snippets
 */
const sanitizeSnippetWithEm = (snippet: string): string =>
  DOMPurify.sanitize(snippet, {
    ALLOWED_TAGS: ['em'],
    ALLOWED_ATTR: [],
  });

// noopener keeps the new page from reaching back into this one.
const openInNewTab = (url: string) => {
  window.open(url, '_blank', 'noopener,noreferrer');
};

const trim = (s: string, maxChars: number) => {
  return s.length > maxChars ? s.substring(0, maxChars) + '...' : s;
};

export {
  trim,
  handlePdfBlob,
  toTimestampString,
  getQueryKey,
  sortByDateDesc,
  toUniqueArrayByKey,
  areArraysEqual,
  shallowEqual,
  sanitizeSnippetWithEm,
  calculateRowsPerPage,
  openInNewTab,
};
